/**
 * Words for the platform's codes that more than one chapter shows (usability audit 2026-10-02,
 * section A; DEC-111). A planner reads words, never a backend code.
 *
 * Every map below is keyed on the codes the platform (or the orchestrator) actually SENDS, each
 * verified at bata-platform v0.61.0 and named in the comment above the map. A code a map does not
 * know is shown as itself — a wrong word is worse than the code, because the word is what a planner
 * audits by — and a test pins every map to its source set (`words.test.ts`).
 *
 * One literal per key, so the catalogue check (`npm run i18n:coverage`) sees every word as used.
 */

/** Anything that resolves a catalogue key — `i18next`'s `t`, narrowed to what these helpers use. */
export type Translate = (key: string) => string;

/** The catalogue key for `code` in `map`, or `null` when this build has no words for it. */
export function wordKey(map: Readonly<Record<string, string>>, code: string | null | undefined): string | null {
  if (code === null || code === undefined) return null;
  return Object.prototype.hasOwnProperty.call(map, code) ? map[code] ?? null : null;
}

/** `code` in words, or the code itself when this build has none. */
export function codeWords(t: Translate, map: Readonly<Record<string, string>>, code: string): string {
  const key = wordKey(map, code);
  return key === null ? code : t(key);
}

// ── the run's entry point (A1) ───────────────────────────────────────────────

/**
 * A run's `entry_point_id`. Two vocabularies reach this field at v0.61.0:
 *
 * - the in-season cycle's runs (`GET /inseason/runs`) carry the NODE ROLE the cycle runs for,
 *   read from the registry row `inseason.entry_point.node_role` — its declared vocabulary is
 *   `("OWN_STORE",)` (`services/repositories/parameter_cards.py`); `OUTLET` is the other node role
 *   the CO location master holds (bata-platform ADR 7014: 295 OWN_STORE, 14 OUTLET);
 * - the allocation engine's own entry points (`AllocationVersionModel`, `AllocateResponseModel`):
 *   `planning/allocation/initial.py`, `inseason_replenishment.py`, `inseason_transfer.py` and
 *   `planning/exit/clearance.py`.
 */
export const ENTRY_POINT_LABEL_KEY: Readonly<Record<string, string>> = {
  OWN_STORE: 'words.entryPoint.ownStore',
  OUTLET: 'words.entryPoint.outlet',
  'launch.initial_allocation': 'words.entryPoint.launchInitialAllocation',
  'inseason.replenishment': 'words.entryPoint.inseasonReplenishment',
  'inseason.push_allocation': 'words.entryPoint.inseasonPushAllocation',
  'inseason.transfer': 'words.entryPoint.inseasonTransfer',
  'exit.clearance': 'words.entryPoint.exitClearance',
};

// ── feeds (A5) ───────────────────────────────────────────────────────────────

/**
 * A feed's `feedId` (`GET /foundations/feeds`, `FeedStatus`). The set is the union of every feed id
 * the estate can land: the 22 feed contracts in bata-data `feeds/contracts/*.yml` (origin/main,
 * 2026-10-02), the `*_FEED_ID` constants bata-platform v0.61.0 reads (`services/repositories/*.py`),
 * and the `FEED_ID`s bata-data's B1 publishers land (`programme_*` included).
 */
export const FEED_LABEL_KEY: Readonly<Record<string, string>> = {
  // bata-data feed contracts
  cost: 'words.feed.cost',
  fx_rate: 'words.feed.fxRate',
  p1_size_level_sales_and_stock: 'words.feed.p1SizeLevelSalesAndStock',
  p2_per_store_stock_history: 'words.feed.p2PerStoreStockHistory',
  p3_product_attribute_master: 'words.feed.p3ProductAttributeMaster',
  p4_cost_margin_at_transaction_grain: 'words.feed.p4CostMarginAtTransactionGrain',
  p5_store_master_attributes: 'words.feed.p5StoreMasterAttributes',
  plm_product_development: 'words.feed.plmProductDevelopment',
  price: 'words.feed.price',
  product_attribute_master: 'words.feed.productAttributeMaster',
  promotion_calendar: 'words.feed.promotionCalendar',
  retail_calendar: 'words.feed.retailCalendar',
  return_event: 'words.feed.returnEvent',
  returns_transaction: 'words.feed.returnsTransaction',
  sales_transaction: 'words.feed.salesTransaction',
  size_master: 'words.feed.sizeMaster',
  stock_movement: 'words.feed.stockMovement',
  stock_position: 'words.feed.stockPosition',
  store_cluster_scheme: 'words.feed.storeClusterScheme',
  store_master: 'words.feed.storeMaster',
  supplier_purchase_order: 'words.feed.supplierPurchaseOrder',
  ticket_price: 'words.feed.ticketPrice',
  // bata-platform landed reads and bata-data B1 publishers
  size_dimension: 'words.feed.sizeDimension',
  location_master: 'words.feed.locationMaster',
  price_observation: 'words.feed.priceObservation',
  price_ladder: 'words.feed.priceLadder',
  landed_cost: 'words.feed.landedCost',
  retail_price: 'words.feed.retailPrice',
  forecast_quantile: 'words.feed.forecastQuantile',
  inseason_lifecycle_curve: 'words.feed.inseasonLifecycleCurve',
  inseason_lifecycle_curve_assignment: 'words.feed.inseasonLifecycleCurveAssignment',
  inseason_reforecast_signal_week: 'words.feed.inseasonReforecastSignalWeek',
  exit_season_intake: 'words.feed.exitSeasonIntake',
  exit_clearance_position: 'words.feed.exitClearancePosition',
  exit_lane_rate_card: 'words.feed.exitLaneRateCard',
  postseason_availability_week: 'words.feed.postseasonAvailabilityWeek',
  postseason_availability_census: 'words.feed.postseasonAvailabilityCensus',
  preseason_size_availability_week: 'words.feed.preseasonSizeAvailabilityWeek',
  product_image_index: 'words.feed.productImageIndex',
  supplier: 'words.feed.supplier',
  purchase_order: 'words.feed.purchaseOrder',
  programme_seasonal_naive_baseline: 'words.feed.programmeSeasonalNaiveBaseline',
  programme_matched_control_panel: 'words.feed.programmeMatchedControlPanel',
  programme_lost_units_at_size_position: 'words.feed.programmeLostUnitsAtSizePosition',
  programme_gate_evidence: 'words.feed.programmeGateEvidence',
  programme_cover_and_terminal_stock: 'words.feed.programmeCoverAndTerminalStock',
  // bata-data B1 publishers (`dagster/assets/*/…_b1.py`, `FEED_ID` / `feed_id=`) the platform lands
  // and `GET /foundations/feeds` lists since the lifecycle, outlet-door and promotion feeds (audit 3).
  article_lifecycle_phase_inferred: 'words.feed.articleLifecyclePhaseInferred',
  commercial_calendar_week: 'words.feed.commercialCalendarWeek',
  demand_exclusion_window: 'words.feed.demandExclusionWindow',
  door_availability_week: 'words.feed.doorAvailabilityWeek',
  outlet_door_position: 'words.feed.outletDoorPosition',
  promotion_article_day: 'words.feed.promotionArticleDay',
  promotion_mechanic_impact: 'words.feed.promotionMechanicImpact',
  promotion_window: 'words.feed.promotionWindow',
  promotion_window_door: 'words.feed.promotionWindowDoor',
  promotion_window_door_subcategory: 'words.feed.promotionWindowDoorSubcategory',
  promotion_window_impact: 'words.feed.promotionWindowImpact',
};

/** What a feed this build has no words for is called; its id is behind the «id» beside it. */
export const FEED_UNNAMED_KEY = 'words.feed.unnamed';

/** A feed in words: its label, else the plain «feed not yet described» (never the id — the caller puts that behind an «id»). */
export function feedWords(t: Translate, feedId: string): string {
  const key = wordKey(FEED_LABEL_KEY, feedId);
  return t(key === null ? FEED_UNNAMED_KEY : key);
}

// ── jobs (A8, A14) ───────────────────────────────────────────────────────────

/**
 * A platform job a screen names: the stages of the three decision cycles (`CYCLES` in
 * `services/api/routers/foundations.py`, v0.61.0) and the jobs a screen waits on (its
 * `AwaitingDataStrip` `source`), each a registered `@job` in `services/jobs/*.py`.
 */
export const JOB_LABEL_KEY: Readonly<Record<string, string>> = {
  'inseason.distribute': 'words.job.inseasonDistribute',
  'inseason.cost_to_serve': 'words.job.inseasonCostToServe',
  'inseason.rebalance': 'words.job.inseasonRebalance',
  'inseason.reforecast': 'words.job.inseasonReforecast',
  'inseason.replan': 'words.job.inseasonReplan',
  'inseason.price': 'words.job.inseasonPrice',
  'inseason.elasticity_fit': 'words.job.inseasonElasticityFit',
  'exit.terminal_stock_measurement': 'words.job.exitTerminalStockMeasurement',
  'exit.consolidation_position': 'words.job.exitConsolidationPosition',
  'exit.plan_record': 'words.job.exitPlanRecord',
  'exit.consolidation_run': 'words.job.exitConsolidationRun',
  'exit.velocity_report': 'words.job.exitVelocityReport',
  'exit.cadence_weekly': 'words.job.exitCadenceWeekly',
  'exit.channel_routing': 'words.job.exitChannelRouting',
  'exit.carry_over_review': 'words.job.exitCarryOverReview',
  'exit.handover_readiness': 'words.job.exitHandoverReadiness',
  'exit.returns_routing': 'words.job.exitReturnsRouting',
  'exit.shadow_backtest': 'words.job.exitShadowBacktest',
  'foundations.exception_sweep': 'words.job.foundationsExceptionSweep',
  'postseason.reconstruct_unconstrained_demand': 'words.job.postseasonReconstructUnconstrainedDemand',
  'postseason.score_suppliers': 'words.job.postseasonScoreSuppliers',
};

/**
 * What a screen waits on when it is not a job: the platform reads a few screens name as their
 * `AwaitingDataStrip` `source` (bata-ui's own spelling of the read, not a platform code).
 */
export const AWAITED_SOURCE_LABEL_KEY: Readonly<Record<string, string>> = {
  ...JOB_LABEL_KEY,
  'foundations.alerts': 'words.source.foundationsAlerts',
  'foundations.feeds': 'words.source.foundationsFeeds',
  'preseason.buy_lines': 'words.source.preseasonBuyLines',
  'preseason.cluster_schemes': 'words.source.preseasonClusterSchemes',
  'preseason.size_curves': 'words.source.preseasonSizeCurves',
};

/**
 * The orchestrator's (Dagster's) words for a schedule, a tick and a run, as `GET
 * /foundations/schedules` relays them verbatim: a schedule's `status` (`RUNNING`/`STOPPED`), a
 * tick's `lastTickStatus` (Dagster `TickStatus`: `STARTED`, `SKIPPED`, `SUCCESS`, `FAILURE`) and a
 * run's `lastRun.status` (Dagster `DagsterRunStatus`) — the enum values of the Dagster bata-data
 * pins, read from its `.venv` (InstigatorStatus, TickStatus, DagsterRunStatus).
 */
export const ORCHESTRATOR_STATUS_KEY: Readonly<Record<string, string>> = {
  RUNNING: 'words.orchestrator.running',
  STOPPED: 'words.orchestrator.stopped',
  DECLARED_IN_CODE: 'words.orchestrator.declaredInCode',
  AUTOMATICALLY_RUNNING: 'words.orchestrator.automaticallyRunning',
  STARTED: 'words.orchestrator.started',
  SKIPPED: 'words.orchestrator.skipped',
  SUCCESS: 'words.orchestrator.success',
  FAILURE: 'words.orchestrator.failure',
  QUEUED: 'words.orchestrator.queued',
  NOT_STARTED: 'words.orchestrator.notStarted',
  MANAGED: 'words.orchestrator.managed',
  STARTING: 'words.orchestrator.starting',
  CANCELING: 'words.orchestrator.canceling',
  CANCELED: 'words.orchestrator.canceled',
};

// ── roles (A9) ───────────────────────────────────────────────────────────────

/**
 * A role. The ten realm roles of `bata-infra/stacks/keycloak/realms/bata-realm.json` (G3.1), and
 * the three approver roles the autonomy register is seeded with
 * (`services/repositories/conformance.py` `DECISION_CLASS_AUTONOMY_SEED`, v0.61.0). A Keycloak
 * built-in (`offline_access`, `default-roles-…`) is shown as its name.
 */
export const ROLE_LABEL_KEY: Readonly<Record<string, string>> = {
  planner: 'words.role.planner',
  senior_planner: 'words.role.seniorPlanner',
  merchandiser: 'words.role.merchandiser',
  allocator: 'words.role.allocator',
  country_manager: 'words.role.countryManager',
  finance: 'words.role.finance',
  supply_chain: 'words.role.supplyChain',
  administrator: 'words.role.administrator',
  auditor: 'words.role.auditor',
  integration_service: 'words.role.integrationService',
  merchandise_director: 'words.role.merchandiseDirector',
  country_planning_manager: 'words.role.countryPlanningManager',
  country_commercial_director: 'words.role.countryCommercialDirector',
  // The delivery team's standalone role: report triage (bata-platform#686, ADR 9063).
  feedback_triage: 'words.role.feedbackTriage',
};

// ── decision classes and autonomy (A10) ──────────────────────────────────────

/**
 * An engine's decision class: the twelve rows of the autonomy register
 * (`DECISION_CLASS_AUTONOMY_SEED`, v0.61.0) and `push_allocate`, the push mode's class
 * (`planning/allocation/inseason_replenishment.py` `DECISION_CLASS_PUSH`).
 */
export const DECISION_CLASS_LABEL_KEY: Readonly<Record<string, string>> = {
  allocate: 'words.decisionClass.allocate',
  buy_depth: 'words.decisionClass.buyDepth',
  cancel: 'words.decisionClass.cancel',
  carry_over: 'words.decisionClass.carryOver',
  chase: 'words.decisionClass.chase',
  chase_cancel: 'words.decisionClass.chaseCancel',
  exit: 'words.decisionClass.exit',
  exit_channel: 'words.decisionClass.exitChannel',
  markdown: 'words.decisionClass.markdown',
  receipt_phasing: 'words.decisionClass.receiptPhasing',
  replenish: 'words.decisionClass.replenish',
  transfer: 'words.decisionClass.transfer',
  push_allocate: 'words.decisionClass.pushAllocate',
};

/** An autonomy level (`planning/principles/autonomy.py`, v0.61.0). */
export const AUTONOMY_LEVEL_LABEL_KEY: Readonly<Record<string, string>> = {
  recommend_only: 'words.autonomyLevel.recommendOnly',
  auto_below_threshold: 'words.autonomyLevel.autoBelowThreshold',
  autonomous: 'words.autonomyLevel.autonomous',
};

// ── the workbook (E2) ────────────────────────────────────────────────────────

/**
 * A label column's `display` for the Excel workbook (DEC-104, usability audit E2): the code in the
 * screen's words, through the same key function the screen uses, or the code itself when there
 * are none — or when the catalogue has no text for the key. The CSV keeps the code (G2.4).
 */
export function exportDisplay(keyOf: (code: string) => string | null): (code: string, translate: (key: string) => string) => string {
  return (code, translate) => {
    const key = keyOf(code);
    if (key === null) return code;
    const text = translate(key);
    return text === '' || text === key ? code : text;
  };
}

/** `exportDisplay` over a code-to-key map. */
export function exportDisplayOf(map: Readonly<Record<string, string>>): (code: string, translate: (key: string) => string) => string {
  return exportDisplay((code) => wordKey(map, code));
}
